import { Config } from "../entities/config";
import {
  DEFAULT_UNREAD_MESSAGES_COUNT,
  EventActions,
  LOGGED_IN_USER_NO_NAME_STATUS,
  LOGGED_OUT_USER_STATUS,
} from "../utility/constants";
import { NaviError, NaviErrorTypes } from "../utility/navi-error";
import { GlobalDataLayerService } from "./analytics/global-data-layer-service";
import { getUserInformation } from "./user-information-service";
import { finderStateStore } from "../state/finder-state-store";
import { navStateStore } from "../state/nav-state-store";
import { UserData } from "../entities/user-information";
import { IsLoggedIn, isString } from "../utility/helper";
import { getAuth0Client } from "./auth-client-service";

export function getUserNameFromUserData(user: UserData | null): string {
  if (!user) return "";
  const { firstName, lastName }: UserData = user;
  return [firstName, lastName].filter(isString).join(" ");
}

export async function initializeUserData(
  accessToken: string,
  locale: string,
): Promise<void> {
  let loggedInState = LOGGED_OUT_USER_STATUS;
  let userId: string;
  let ciamid: string;

  try {
    const bffData = await getUserInformation(accessToken, locale);

    if (bffData === undefined || bffData === null) {
      throw new NaviError(
        `Invalid user data object extracted from user profile response`,
        NaviErrorTypes.NO_RESULTS,
      );
    }

    finderStateStore.state.savedSearchesCount = bffData.savedSearches;
    finderStateStore.state.savedVehiclesCount = bffData.savedVehicles;
    navStateStore.state.unreadMessagesCount = bffData.unreadMessages;
    const { userData } = bffData;

    loggedInState =
      getUserNameFromUserData(userData) || LOGGED_IN_USER_NO_NAME_STATUS;
    userId = bffData.userData.userId ?? "";
    ciamid = bffData.userData.ciamId ?? "";
    if (loggedInState) {
      GlobalDataLayerService.getInstance().setUser(
        userId,
        ciamid,
        IsLoggedIn(loggedInState),
      );
      GlobalDataLayerService.getInstance().pushDataLayerEvent(
        EventActions.IDS_LOAD,
      );
    }
  } catch (err) {
    if (
      !(err instanceof NaviError) ||
      err.type !== NaviErrorTypes.UNAUTHORIZED
    ) {
      loggedInState = LOGGED_IN_USER_NO_NAME_STATUS;
    }

    navStateStore.state.unreadMessagesCount = DEFAULT_UNREAD_MESSAGES_COUNT;
    navStateStore.state.loggedInState = loggedInState;
    throw err;
  }

  navStateStore.state.loggedInState = loggedInState;
  GlobalDataLayerService.getInstance().setUser(
    userId,
    ciamid,
    IsLoggedIn(loggedInState),
  );
}

export async function logout(
  config: Config,
  redirectUrl: string,
): Promise<void> {
  const auth0Client = getAuth0Client(config);
  await auth0Client.logout({
    openUrl() {
      window.location.replace(redirectUrl);
    },
  });
}

export async function loginWithRedirect(config: Config): Promise<void> {
  const auth0Client = getAuth0Client(config);

  await auth0Client.loginWithRedirect({
    appState: { path: window.location.pathname, query: window.location.search },
  });
}

export async function getTokenSilently(
  config: Config,
): Promise<string | { token: null; error: string }> {
  try {
    const auth0Client = getAuth0Client(config);
    const token = await auth0Client.getTokenSilently();
    window.dispatchEvent(
      new CustomEvent("getToken", {
        detail: { token },
      }),
    );
    return token;
  } catch (err) {
    return {
      token: null,
      error: JSON.stringify(err),
    };
  }
}
